import { c as _c } from "react/compiler-runtime";
import { Avatar, Box, Button, Flex, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm';
import { ClarityLinkCodeStep } from '@src/components/sco/Advisors/clarityLink/ClarityLinkCodeStep';
import { type ClarityAdvisorImportProof } from '@src/components/sco/Advisors/hooks/useClarityAdvisorImport';
import { ClarityAdvisorImportHoldDialog } from '@src/components/sco/Advisors/sections/ClarityAdvisorImportHoldDialog';
import { type ClarityAdvisorImportHoldPhase } from '@src/components/sco/Advisors/sections/clarityAdvisorImportHold';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { TextLink } from '@src/components/sco/TextLink';
import { ScoClarityAdvisorImportClaimStatus } from '@src/graphql-types';
import { ReactNode } from 'react';
export type ClarityAdvisorImportExpert = {
  fullName?: string | null;
  firstName?: string | null;
  screenName?: string | null;
  bio?: string | null;
  location?: string | null;
  imageUrl?: string | null;
  hourlyRate?: number | null;
  clarityProfileUrl?: string | null;
  shortBio?: string | null;
};
export type ClarityAdvisorImportViewProps = {
  handle: string;
  claimStatus: ScoClarityAdvisorImportClaimStatus;
  expert: ClarityAdvisorImportExpert | null;
  isRegistered: boolean;
  claiming?: boolean;
  extracting?: boolean;
  holdPhase?: ClarityAdvisorImportHoldPhase;
  error?: string | null;
  loginHref: string;
  applyHref: string;
  becomeHref: string;
  onConfirm?: () => void;
  /** Clarity link plan (D2–D4): blocked message, code hint, or the code step. */
  proof?: ClarityAdvisorImportProof | null;
  sendingCode?: boolean;
  /** Override guest signup (Storybook). Production omits this. */
  guestSignup?: ReactNode;
  /** Compact layout for the landing diagnostic card (no page chrome). */
  embedded?: boolean;
};
const rateLabel = (rate?: number | null) => {
  if (rate == null || Number.isNaN(Number(rate))) {
    return null;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Math.round(Number(rate)));
};
const PageShell = t0 => {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [10, 12];
    t2 = [8, 10];
    t3 = [5, 8, 10];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <Flex align="center" direction="column" justify="center" minHeight="full" paddingBottom={t1} paddingTop={t2} paddingX={t3} width="full"><Box maxWidth="lg" width="full">{children}</Box></Flex>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};
const EmbeddedShell = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Box textAlign="left" width="full">{children}</Box>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const ClarityAdvisorImportView = t0 => {
  const $ = _c(81);
  const {
    handle,
    claimStatus,
    expert,
    isRegistered,
    claiming: t1,
    extracting: t2,
    holdPhase,
    error: t3,
    loginHref,
    applyHref,
    becomeHref,
    onConfirm,
    proof: t4,
    sendingCode: t5,
    guestSignup,
    embedded: t6
  } = t0;
  const claiming = t1 === undefined ? false : t1;
  const extracting = t2 === undefined ? false : t2;
  const error = t3 === undefined ? null : t3;
  const proof = t4 === undefined ? null : t4;
  const sendingCode = t5 === undefined ? false : t5;
  const embedded = t6 === undefined ? false : t6;
  const phase = holdPhase || (extracting ? "extracting" : claiming ? "claiming" : "idle");
  const busy = phase !== "idle";
  let t7;
  if ($[0] !== expert?.firstName) {
    t7 = expert?.firstName?.trim();
    $[0] = expert?.firstName;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const firstName = t7;
  const name = expert?.fullName || firstName || handle;
  const headingName = firstName || name;
  let t8;
  if ($[2] !== expert?.shortBio) {
    const shortBio = expert?.shortBio?.trim();
    t8 = shortBio && shortBio.toLowerCase() !== "clarity expert" ? shortBio : null;
    $[2] = expert?.shortBio;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const blurb = t8;
  const t9 = expert?.hourlyRate;
  let t10;
  if ($[4] !== t9) {
    t10 = rateLabel(t9);
    $[4] = t9;
    $[5] = t10;
  } else {
    t10 = $[5];
  }
  const rate = t10;
  const Shell = embedded ? EmbeddedShell : PageShell;
  if (claimStatus === ScoClarityAdvisorImportClaimStatus.NotFound || claimStatus === ScoClarityAdvisorImportClaimStatus.NotExpert) {
    let t11;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Heading fontSize={["2xl", "3xl"]} letterSpacing="-0.03em">We couldn't find that profile</Heading>;
      $[6] = t11;
    } else {
      t11 = $[6];
    }
    const t12 = claimStatus === ScoClarityAdvisorImportClaimStatus.NotExpert ? "That Clarity account isn't set up as an expert." : "There's no Clarity expert at this link.";
    let t13;
    if ($[7] !== t12) {
      t13 = <VStack align="stretch" gap={2}>{t11}<Text color="fg.muted" fontFamily="longform" textStyle="md">{t12}{" "}You can still apply to advise on Startups.com.</Text></VStack>;
      $[7] = t12;
      $[8] = t13;
    } else {
      t13 = $[8];
    }
    let t14;
    if ($[9] !== becomeHref) {
      t14 = <ButtonLink href={becomeHref} variant="cta" width="full">Apply to advise</ButtonLink>;
      $[9] = becomeHref;
      $[10] = t14;
    } else {
      t14 = $[10];
    }
    let t15;
    if ($[11] !== t13 || $[12] !== t14) {
      t15 = <VStack align="stretch" gap={5}>{t13}{t14}</VStack>;
      $[11] = t13;
      $[12] = t14;
      $[13] = t15;
    } else {
      t15 = $[13];
    }
    let t16;
    if ($[14] !== Shell || $[15] !== t15) {
      t16 = <Shell>{t15}</Shell>;
      $[14] = Shell;
      $[15] = t15;
      $[16] = t16;
    } else {
      t16 = $[16];
    }
    return t16;
  }
  const t11 = embedded ? 4 : 6;
  let t12;
  if ($[17] !== embedded) {
    t12 = embedded ? ["xl", "2xl"] : ["2xl", "3xl"];
    $[17] = embedded;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const t13 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? `Welcome back, ${headingName}` : `${headingName}, is this you?`;
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <Heading fontSize={t12} letterSpacing="-0.03em">{t13}</Heading>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? "Finish your advisor application whenever you're ready." : "We'll start your application from this profile.";
  let t16;
  if ($[22] !== t15) {
    t16 = <Text color="fg.muted" fontFamily="longform" textStyle="md">{t15}</Text>;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t11 || $[25] !== t14 || $[26] !== t16) {
    t17 = <VStack align="stretch" gap={2} marginBottom={t11}>{t14}{t16}</VStack>;
    $[24] = t11;
    $[25] = t14;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== embedded) {
    t18 = embedded ? 6 : [8, 10];
    $[28] = embedded;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== name) {
    t19 = <Avatar.Fallback name={name} />;
    $[30] = name;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== expert || $[33] !== name) {
    t20 = expert?.imageUrl ? <Avatar.Image alt={name} src={expert.imageUrl} /> : null;
    $[32] = expert;
    $[33] = name;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t19 || $[36] !== t20) {
    t21 = <Avatar.Root flexShrink={0} size="xl">{t19}{t20}</Avatar.Root>;
    $[35] = t19;
    $[36] = t20;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== name) {
    t22 = <Text fontWeight="semibold">{name}</Text>;
    $[38] = name;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== expert) {
    t23 = expert?.location ? <Text color="fg.muted" fontSize="sm">{expert.location}</Text> : null;
    $[40] = expert;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== blurb) {
    t24 = blurb ? <Text color="fg.muted" fontSize="sm">{blurb}</Text> : null;
    $[42] = blurb;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== rate) {
    t25 = rate ? <Text color="fg.muted" fontSize="sm">{rate}/hr on Clarity</Text> : null;
    $[44] = rate;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== expert) {
    t26 = expert?.clarityProfileUrl ? <TextLink href={expert.clarityProfileUrl} target="_blank" width="fit-content">View on Clarity.fm</TextLink> : null;
    $[46] = expert;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  let t27;
  if ($[48] !== t22 || $[49] !== t23 || $[50] !== t24 || $[51] !== t25 || $[52] !== t26) {
    t27 = <VStack align="flex-start" gap={1} minWidth={0}>{t22}{t23}{t24}{t25}{t26}</VStack>;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
    $[51] = t25;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t18 || $[55] !== t21 || $[56] !== t27) {
    t28 = <HStack align="start" gap={4} marginBottom={t18}>{t21}{t27}</HStack>;
    $[54] = t18;
    $[55] = t21;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== error) {
    t29 = error ? <Text color="red.fg" fontSize="sm" marginBottom={4}>{error}</Text> : null;
    $[58] = error;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== applyHref || $[61] !== busy || $[62] !== claimStatus || $[63] !== guestSignup || $[64] !== isRegistered || $[65] !== loginHref || $[66] !== onConfirm || $[67] !== proof || $[68] !== sendingCode) {
    t30 = claimStatus === ScoClarityAdvisorImportClaimStatus.ClaimedByMe ? <ButtonLink href={applyHref} variant="cta" width="full">Continue your application</ButtonLink> : isRegistered && proof?.codeStep ? <ClarityLinkCodeStep {...proof.codeStep} /> : isRegistered ? <VStack align="stretch" gap={3}>{proof?.blockedMessage ? <Text color="fg.muted" fontSize="sm">{proof.blockedMessage}</Text> : null}<Button disabled={busy || !onConfirm} loading={busy || sendingCode} variant="cta" width="full" onClick={onConfirm}>Continue with this profile</Button>{proof?.hint ? <Text color="fg.muted" fontSize="sm" textAlign="center">{proof.hint}</Text> : null}</VStack> : <VStack align="stretch" gap={4}>{guestSignup ?? <UserSignupEmailCaptureForm brand="clarity" hubspotFormKey="scom-advisors-clarity-import" socialLoginButtonsProps={{
        buttonLayout: "google-only"
      }} submitButtonProps={{
        children: "Create account",
        size: "lg"
      }} containerProps={{
        bg: "transparent",
        borderWidth: "0",
        boxShadow: "none",
        padding: 0,
        width: "full"
      }} />}<Text color="fg.muted" fontSize="sm" textAlign="center">Already have an account? <TextLink href={loginHref}>Log in</TextLink></Text></VStack>;
    $[60] = applyHref;
    $[61] = busy;
    $[62] = claimStatus;
    $[63] = guestSignup;
    $[64] = isRegistered;
    $[65] = loginHref;
    $[66] = onConfirm;
    $[67] = proof;
    $[68] = sendingCode;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== phase) {
    t31 = <ClarityAdvisorImportHoldDialog phase={phase} />;
    $[70] = phase;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t17 || $[73] !== t28 || $[74] !== t29 || $[75] !== t30 || $[76] !== t31) {
    t32 = <VStack align="stretch" gap={0}>{t17}{t28}{t29}{t30}{t31}</VStack>;
    $[72] = t17;
    $[73] = t28;
    $[74] = t29;
    $[75] = t30;
    $[76] = t31;
    $[77] = t32;
  } else {
    t32 = $[77];
  }
  let t33;
  if ($[78] !== Shell || $[79] !== t32) {
    t33 = <Shell>{t32}</Shell>;
    $[78] = Shell;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  return t33;
};